import React, { useEffect } from 'react';
// Switch was renamed to routes, which causes issues as that is what our main
// component is called. Alias it as a solution.
import { Route, Routes as Switch, useLocation } from 'react-router-dom';
import { useMatomo } from '@jonkoops/matomo-tracker-react'
import { useAppContext } from './libs/contextLib';
import AuthenticatedRoute from "./components/AuthenticatedRoute";
import UnauthenticatedRoute from "./components/UnauthenticatedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import LazyComponent from "./components/LazyComponent";
import { userTypes } from './libs/userLib';

// Unauthenticated.
import Login from './containers/Login';
import Register from './containers/Register';
import RequestAccount from './containers/RequestAccount';
import SubmitClaim from './containers/SubmitClaim';
import Confirm from './containers/Confirm';
import ForgotPassword from './containers/ForgotPassword';
import UserDeleted from './containers/UserDeleted';
import ErrorThrower from './components/ErrorThrower';
import NotFound from './containers/NotFound';

import config from './config';

// Authenticated.
// We'll lazy load these.
// See: https://reactjs.org/docs/code-splitting.html
const Home                = React.lazy(() => import('./containers/Home'));

const User                = React.lazy(() => import('./containers/User'));
const ChangeEmail         = React.lazy(() => import('./containers/ChangeEmail'));
const ChangePassword      = React.lazy(() => import('./containers/ChangePassword'));
const Logout              = React.lazy(() => import('./containers/Logout'));
const SetType             = React.lazy(() => import('./containers/SetType'));

const Identity            = React.lazy(() => import('./containers/Identity'));
const EditIdentity        = React.lazy(() => import('./containers/EditIdentity'));

const Bank                = React.lazy(() => import('./containers/Bank'));
const Email               = React.lazy(() => import('./containers/Email'));
const Address             = React.lazy(() => import('./containers/Address'));

//const FcaFirm             = React.lazy(() => import('./containers/fca/FcaFirm')); 
//const FcaIndividual       = React.lazy(() => import('./containers/fca/FcaIndividual'));
//const FcaSearch           = React.lazy(() => import('./containers/fca/FcaSearch')); 

const RiskClient          = React.lazy(() => import('./containers/risk/RiskClient'));
const RiskLog             = React.lazy(() => import('./containers/risk/RiskLog'));

const ConfigOverrides     = React.lazy(() => import('./containers/ConfigOverrides'));

const EditNotifications   = React.lazy(() => import('./containers/EditNotifications'));
const EditNotification    = React.lazy(() => import('./containers/EditNotification'));

const ListFormData        = React.lazy(() => import('./containers/ListFormData'));

const ScheduleEnvironment = React.lazy(() => import('./containers/ScheduleEnvironment'));

const Cats                = React.lazy(() => import('./containers/Cats'));


export default function Routes() {
  const location          = useLocation();
  const { clearErrors }   = useAppContext();
  const { trackPageView } = useMatomo();

  // A change in location will trigger this useEffect which'll clean errors
  // and send an event to Matomo.
  useEffect(() => {
    //Track the page change.
    trackPageView();
    // Clear any non-persistent errors.
    clearErrors();
    // eslint-disable-next-line
  },[location]) 

  return(
    <ErrorBoundary>
      <LazyComponent>
        <Switch>
          <Route
            exact="true"
            path="/"
            element={
              <AuthenticatedRoute>
                <Home />
              </AuthenticatedRoute>
            }
          />
          {/* User */}
          <Route
            exact="true"
            path="/user"
            element={
              <AuthenticatedRoute>
                <User />
              </AuthenticatedRoute>
            }
          />
          <Route
            exact="true"
            path="/user/email"
            element={
              <AuthenticatedRoute>
                <ChangeEmail />
              </AuthenticatedRoute>
            }
          />
          <Route
            exact="true"
            path="/user/password"
            element={
              <AuthenticatedRoute>
                <ChangePassword />
              </AuthenticatedRoute>
            }
          />
          <Route
            exact="true"
            path="/user/settype"
            element={
              <AuthenticatedRoute
                authorisedTypes = {
                  [
                    userTypes.undiff,
                    userTypes.administrator,
                    userTypes.paraplanner,
                    userTypes.ifa,
                  ]
                }
              >
                <SetType />
              </AuthenticatedRoute>
            }
          />
          <Route
            exact="true"
            path="/logout"
            element={
              <AuthenticatedRoute>
                <Logout />
              </AuthenticatedRoute>
            }
          />
          {/* Identity */}
          <Route
            exact="true"
            path="/identity"
            element={
              <AuthenticatedRoute
                authorisedTypes = {[userTypes.staff]}
              >
                <Identity />
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/identity/:username"
            element={
              <AuthenticatedRoute
                authorisedTypes = {[userTypes.staff]}
              >
                <EditIdentity />
              </AuthenticatedRoute>
            }
          />
          {/* Lookups */}
          <Route
            exact="true"
            path="/bank"
            element={
              <AuthenticatedRoute>
                <Bank />
              </AuthenticatedRoute>
            }
          />
          <Route
            exact="true"
            path="/email"
            element={
              <AuthenticatedRoute>
                <Email />
              </AuthenticatedRoute>
            }
          />
          <Route
            exact="true"
            path="/address"
            element={
              <AuthenticatedRoute>
                <Address />
              </AuthenticatedRoute>
            }
          />
          {/* Risk */}
          <Route
            path="/risk/log/:reference"
            element={
              <AuthenticatedRoute
                authorisedTypes = {[userTypes.staff]}
                authorisedDepartments = {[
                  'Information & Technology',
                  'Technology',
                  'IT',
                  'Risk',
                  'Risk & Compliance',
                  'Business Processing',
                  'Operations Support',
                  'Accounts',
                  'Claims',
                  'Business Change',
                  'Strategy & Transformation',
                  'Business Change & Transformation',
                  'Project Fusion',
                  'Member Services'
                ]}
              >
                <RiskLog />
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/risk/log"
            element={
              <AuthenticatedRoute
                authorisedTypes = {[userTypes.staff]}
                authorisedDepartments = {[
                  'Information & Technology',
                  'Technology',
                  'IT',
                  'Risk',
                  'Risk & Compliance',
                  'Business Processing',
                  'Operations Support',
                  'Accounts',
                  'Claims',
                  'Business Change',
                  'Strategy & Transformation',
                  'Business Change & Transformation',
                  'Project Fusion',
                  'Member Services'
                ]}
              >
                <RiskLog />
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/risk/client"
            element={
              <AuthenticatedRoute
                authorisedTypes = {[userTypes.staff]}
              >
                <RiskClient />
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/configOverrides"
            element={
              <AuthenticatedRoute
                authorisedTypes = {[userTypes.staff]}
              >
                <ConfigOverrides />
              </AuthenticatedRoute>
            }
          />
          {/* Notifications */}
          <Route
            path="/notifications/:id"
            element={
              <AuthenticatedRoute
                authorisedTypes = {[userTypes.staff]}
              >
                <EditNotification />
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <AuthenticatedRoute
                authorisedTypes = {[userTypes.staff]}
                authorisedDepartments = {[
                  'Information & Technology',
                  'Technology',
                  'IT',
                  'Developers',
                  'Marketing',
                  'Business Change',
                  'Strategy & Transformation',
                  'Business Change & Transformation',
                  'Project Fusion',
                ]}
              >
                <EditNotifications />
              </AuthenticatedRoute>
            }
          />
          {/* FormData */}
          <Route
            path = "/accountrequests"
            title = "Account requests"
            element={
              <AuthenticatedRoute
                authorisedTypes = {[userTypes.staff]}
                authorisedDepartments = {[
                  'Information & Technology',
                  'Technology',
                  'IT',
                  'Developers',
                  'Business Change',
                  'Strategy & Transformation',
                  'Business Change & Transformation',
                  'Project Fusion',
                  'Risk',
                  'Risk & Compliance',
                  'Business Processing',
                  'Customer Care',
                  'Agency Services',
                  'Adviser Services',
                  'Operations Support',
                  'Distribution Operations'
                ]}
              >
                <ListFormData
                  formId = "1"
                  keyRewrites = {{
                    title: "Title",
                    firstName: "First name",
                    lastName: "Last name",
                    type: "Role type",
                    irn: "IRN",
                    postcode: "Postcode",
                    addressLine1: "Address Line 1",
                    addressLine2: "Address Line 2",
                    addressTown: "Town",
                    addressCounty: "County",
                    addressCountry: "Country",
                    email: "Email",
                    mobile: "Business Mobile Phone",
                    landline: "Business Landline Number",
                    farFrn: "Appointed Representative FRN",
                    fdaFrn: "Directly Authorised FRN",
                    serviceProvider: "Service Provider",
                    internalAgencyCode: "Internal Agency Code",
                    otherFirms: "Do you sell/service on behalf of other firms or submitted to us in the past?",
                    otherFirmsNew: "Do you currently sell or service on behalf of any other firms working with Cirencester Friendly Society?",
                    submittedPast: "Have you submitted to us in the past?",
                    notes: "Further information"
                  }}
                  statusOptions = {[
                    "submitted",
                    "processing",
                    "Processing - TOBS Required",
                    "Processing - TOBS Review",
                    "Processing - SP TOBs required",
                    "Processing - Awaiting link to SP",
                    "Processing - Await Firm Information",
                    "Processing - Await Adviser Information",
                    "Processing - Await Risk",
                    "Processing - SP TOBs Review",
                    "Processing - Await de-link",
                    "onboarded",
                    "rejected",
                    "expired",
                  ]}
                  statusColours = {
                    {
                      "submitted": "#721d75",
                      "processing": "#0dcaf0",
                      "Processing - TOBS Required": "#e9bb20",
                      "Processing - TOBS Review": "#e9bb20",
                      "Processing - SP TOBs required": "#e9bb20",
                      "Processing - Awaiting link to SP": "#e9bb20",
                      "Processing - Await Firm Information": "#e9bb20",
                      "Processing - Await Adviser Information": "#e9bb20",
                      "Processing - Await Risk": "#e9bb20",
                      "Processing - SP TOBs Review": "#e9bb20",
                      "Processing - Await de-link": "#e9bb20",
                      "onboarded": "#198754",
                      "rejected": "#dc3545",
                      "expired": "#dc3545",
                    }
                  }
                />
              </AuthenticatedRoute>
            }
          />
          <Route
            path = "/submittedClaims"
            title = "Submitted claims"
            element={
              <AuthenticatedRoute
                authorisedTypes = {[userTypes.staff]}
                authorisedDepartments = {[
                  'Information & Technology',
                  'Technology',
                  'IT',
                  'Developers',
                  'Business Change',
                  'Strategy & Transformation',
                  'Business Change & Transformation',
                  'Project Fusion',
                  'Claims'
                ]}
              >
                {
                  config.featureFlags.submitClaim ?
                  <ListFormData
                    formId = "2"
                    title = "Submitted claims"
                    keyRewrites = {{
                      areYouMember: 'Are you the member?',
                      formFilledOutByRelationship:'Relationship to claimant',
                      formFilledOutByName: 'Form filled by',
                      member_contractNumber: 'Contract Number',
                      member_title: 'Title',
                      member_firstName: 'First Name',
                      member_lastName: 'Last Name',
                      member_postcode: 'Postcode',
                      member_addressLine1: 'Address 1',
                      member_addressLine2: 'Address 2',
                      member_town: 'Town',
                      member_county: 'County',
                      member_country: 'Country',
                      member_telephone: 'Telephone',
                      member_email: 'Email',
                      member_contactPreferencePhone: 'Contact by phone',
                      member_contactPreferenceEmail: 'Contact by email',
                      member_contactPreferencePost: 'Contact by post',
                      member_DOB: 'Date of Birth',
                      illnessInjury_what: 'What happened',
                      illnessInjury_how: 'How did it happen',
                      illnessInjury_prevent_working: 'How has this prevented the claimant from working',
                      illnessInjury_when: 'When did the injury occur',
                      illnessInjury_where: 'Geographically where did the injury occur',
                      illnessInjury_history: 'Do you have prior History',
                      illnessInjury_consultation: 'Have you consulted with an expert',
                      doctor_firstName: 'Doctor First Name',
                      doctor_lastName: 'Doctor Last Name',
                      doctor_telephone: 'Doctor Last Telephone',
                      doctor_postcode: 'Doctor Postcode',
                      doctor_addressLine1: 'Doctor Address 1',
                      doctor_addressLine2: 'Doctor Address 2',
                      doctor_town: 'Doctor Town',
                      doctor_county: 'Doctor County',
                      doctor_country: 'Doctor Country',
                      doctor_changed: 'Has your Doctor changed',
                      doctor_changed_firstName: 'Previous Doctor First Name',
                      doctor_changed_lastName: 'Previous Doctor Last Name',
                      doctor_changed_telephone: 'Previous Doctor Telephone',
                      doctor_changed_postcode: 'Previous Doctor Postcode',
                      doctor_changed_addressLine1: 'Previous Doctor Address Line 1',
                      doctor_changed_addressLine2: 'Previous Doctor Address Line 2',
                      doctor_changed_town: 'Previous Doctor Address Town',
                      doctor_changed_county: 'Previous Doctor Address County',
                      doctor_changed_country: 'Previous Doctor Address Country',
                      illnessInjury_treatment: 'Treatment',
                      illnessInjury_hospitalised: 'Have you been Hospitalised',
                      occupation_main_origo: 'Main Occupation',
                      occupation_main_further: 'Main Occupation more detail',
                      occupation_status: 'Occupation Status',
                      occupation_employer_name: 'Employer Name',
                      occupation_employer_telephone: 'Employer Telephone',
                      occupation_employer_email: 'Employer Email',
                      occupation_postcode: 'Employer Postcode',
                      occupation_addressLine1: 'Employer Address Line 1',
                      occupation_addressLine2: 'Employer Address Line 2',
                      occupation_town: 'Employer Address Town',
                      occupation_county: 'Employer Address County',
                      occupation_further_accountantholdrecords: 'Does you accountant hold financial records',
                      occupation_accountant_name: 'Accountant Name',
                      occupation_accountant_telephone: 'Accountant Telephone',
                      occupation_accountant_email: 'Accountant Email',
                      occupation_accountant_postcode: 'Accountant Postcode',
                      occupation_accountant_addressLine1: 'Accountant Address Line 1',
                      occupation_accountant_address_Line2: 'Accountant Address Line 2',
                      occupation_accountant_town: 'Accountant Town',
                      occupation_accountant_county: 'Accountant County',
                      occupation_2nd: 'Do you have a secondary occupation',
                      occupation_2nd_origo: 'Secondary occupation',
                      occupation_2nd_further: 'Secondary occupation more detail',
                      occupation_2nd_status: 'Secondary occupation status',
                      occupation_2nd_name: 'Secondary Employer Name',
                      occupation_2nd_telephone: 'Secondary Employer Telephone',
                      occupation_2nd_email: 'Secondary Employer Email',
                      occupation_2nd_postcode: 'Secondary employer postcode',
                      occupation_2nd_addressLine1: 'Secondary employer address line 1',
                      occupation_2nd_addressLine2: 'Secondary employer address line 2',
                      occupation_2nd_town: 'Secondary employer address town',
                      occupation_2nd_county: 'Secondary employer address county',
                      occupation_2nd_further_accountantholdrecords: 'Does you second accountant hold financial records',
                      occupation_2nd_accountant_name: 'Second Accountant Name',
                      occupation_2nd_accountant_telephone: 'Second Accountant Telephone',
                      occupation_2nd_accountant_email: 'Second Accountant Email',
                      occupation_2nd_accountant_postcode: 'Second Accountant Postcode',
                      occupation_2nd_accountant_addressLine1: 'Second Accountant Address Line 1',
                      occupation_2nd_accountant_addressLine2: 'Second Accountant Address Line 2',
                      occupation_2nd_accountant_town: 'Second Accountant Town',
                      occupation_2nd_accountant_county: 'Second Accountant County',
                      occupation_more: 'Do you have more than two occupations',
                      occupation_changed12Months: 'Has your occupation changed in the last 12 months',
                      benefit_continuedEarningsSSP: 'Continued Earnings including Statutory Sick Pay',
                      benefit_EarlyRetirement: 'Early Retirement income',
                      benefit_workPensions: 'Works and Pension',
                      benefit_otherInsurance: 'Other Insurance',
                      payments: 'Where would you want the payment to be made',
                      furtherInformation: 'Any Further information',
                      furtherInformation_more: 'Further Information',
                      discussClaimOthers: 'Discuss with third party',
                      discussClaimOthers_more: 'Third party details',
                    }}
                    statusOptions = {[
                      "submitted",
                      "processed",
                    ]}
                    statusColours = {
                      {
                        "submitted": "#721d75",
                        "processed": "#198754",
                      }
                    }
                  /> : <NotFound />
                }
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/scheduleEnvironment"
            element={
              <AuthenticatedRoute
                authorisedTypes = {[userTypes.staff]}
                authorisedDepartments = {[
                  'Information & Technology',
                  'Technology',
                  'IT',
                  'Business Change',
                  'Strategy & Transformation',
                  'Business Change & Transformation',
                  'Project Fusion',
                  'Developers',
                  'Underwriting'
                ]}
              >
                <ScheduleEnvironment />
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/cats"
            element={
              <AuthenticatedRoute
                authorisedTypes = {[userTypes.staff]}
                authorisedDepartments = {[
                  'Information & Technology',
                  'Technology',
                  'IT',
                  'Business Change',
                  'Strategy & Transformation',
                  'Business Change & Transformation',
                  'Project Fusion',
                  'Developers',
                ]}
              >
                <Cats />
              </AuthenticatedRoute>
            }
          />
          {/* Unauthenticated */}
          <Route
            exact="true"
            path="/login/:userType"
            element={
                <UnauthenticatedRoute>
                  <Login />
                </UnauthenticatedRoute>
            }
          />
          <Route
            exact="true"
            path="/login"
            element={
              <UnauthenticatedRoute>
                <Login />
              </UnauthenticatedRoute>
            }
          />
          <Route
            exact="true"
            path="/register"
            element={
              <UnauthenticatedRoute>
                {
                  config.featureFlags.disableSelfSignup ?
                  <RequestAccount />
                  :
                  <Register />
                }
              </UnauthenticatedRoute>
            }
          />
          <Route
            exact="true"
            path="/submitClaim"
            element={
              config.featureFlags.submitClaim ?
              <SubmitClaim />
              :
              <NotFound />
            }
          />
          <Route
            exact="true"
            path="/confirm"
            element={
              <UnauthenticatedRoute>
                <Confirm />
              </UnauthenticatedRoute>
            }  
          />
          <Route
            exact="true"
            path="/forgotpassword"
            element={
              <UnauthenticatedRoute>
                <ForgotPassword />
              </UnauthenticatedRoute>
            }
          />
          <Route
            exact="true"
            path="/userdeleted"
            element={
              <UnauthenticatedRoute>
                <UserDeleted />
              </UnauthenticatedRoute>
            }
          />
          <Route
            exact="true"
            path="/errorThrower"
            element={
              <UnauthenticatedRoute>
                <ErrorThrower />
              </UnauthenticatedRoute>
            }
          />
          {/* Catch all unmatched routes. */}
          <Route
            element={
              <NotFound />
            }
          />
        </Switch>
      </LazyComponent>
    </ErrorBoundary>
  );
}
